import React from 'react'
import { MILLISECONDS_PER_CHUNK } from '@mote/helpers/constants'
import { __TEST__ } from '@mote/helpers/constants'
import { DiagnosticsLogger } from '@mote/helpers/diagnostics'
import { getResourceFile } from '@mote/helpers/misc'
import { useFlightRecorder } from '../../recorder/state/useFlightRecorder'
import { useMediaUploader } from './useMediaUploader'

export const RECORDER_STATUS = Object.freeze({
  inactive: 'inactive',
  recording: 'recording',
  paused: 'paused',
  stopped: 'stopped',
  cancelled: 'cancelled'
})

const defaultOptions = {
  parentMoteId: null,
  maxSeconds: 30,
  shouldRedirect: false,
  canBeSticky: false
}

const logD = DiagnosticsLogger('useRecorder', 'recorder')

export const useRecorder = (options = defaultOptions) => {
  const mergedOptions = Object.assign(defaultOptions, options)
  const {
    streamingTranscriptData,
    registeredMote,
    register,
    sendChunk,
    finalize,
    pause: pauseRecording,
    finalizeData,
    cleanup: uploaderCleanup,
    error: uploaderError
  } = useMediaUploader(
    mergedOptions.parentMoteId,
    mergedOptions.shouldRedirect,
    mergedOptions.getExtraPayload
  )
  const { flightState, resetFlightState } = useFlightRecorder()

  const [status, setStatus] = React.useState(RECORDER_STATUS.inactive)
  const [error, setError] = React.useState(null)

  const stream = React.useRef(null)
  const recorder = React.useRef(null)
  const chunkCount = React.useRef(null)

  const maxChunkCount =
    (mergedOptions.maxSeconds * 1000) / MILLISECONDS_PER_CHUNK

  async function getStream() {
    try {
      const audioStream = await window.navigator.mediaDevices.getUserMedia({
        audio: true
      })

      stream.current = audioStream

      audioStream
        ? logD.info('Got audiostream from window.navigator')
        : logD.error('Did not receive audiostream from window.navigator')
    } catch (error) {
      setError(error)
    }
  }

  async function start() {
    resetFlightState()
    cleanup()
    register()

    if (!stream.current) await getStream()

    const resolvedStream = stream.current

    if (!__TEST__ && !(resolvedStream instanceof MediaStream)) return null

    recorder.current = new MediaRecorder(resolvedStream)
    recorder.current.addEventListener('dataavailable', e => {
      // Recorder when you cancel it throws one last chunk which is messing
      // subsequent recordings hence the condition here
      if (!recorder.current) return
      handleChunk(e)
    })
    recorder.current.addEventListener('stop', () => stop())
    recorder.current.start(MILLISECONDS_PER_CHUNK)
    setStatus(RECORDER_STATUS.recording)

    logD.info('Recording started')
    return resolvedStream
  }

  function pause() {
    if (recorder.current?.state !== RECORDER_STATUS.recording) return
    recorder.current.pause()
    setStatus(RECORDER_STATUS.paused)

    pauseRecording()

    logD.info('Paused recording')
  }

  function resume() {
    if (recorder.current?.state !== RECORDER_STATUS.paused) return
    recorder.current.resume()
    setStatus(RECORDER_STATUS.recording)

    logD.info('Resumed recording')
  }

  async function stop(additionalPayload = {}) {
    if (
      !recorder.current ||
      recorder.current.state === RECORDER_STATUS.inactive
    )
      return
    playSound('finish-recording')
    recorder.current.stop()

    setStatus(RECORDER_STATUS.stopped)
    logD.info('Stopped recording')

    if (stream.current) {
      stream.current.getTracks().forEach(track => track.stop())
      stream.current = null
      removeRecorderListeners()
    }

    if (!registeredMote) {
      logD.info('Mote not registered yet')
      return false
    } else {
      if (Object.keys(flightState.requests).length > 0) {
        return false
      } else {
        return await finalize(additionalPayload)
      }
    }
  }

  function resetStatus() {
    setStatus(RECORDER_STATUS.inactive)
  }

  function cancel(options) {
    if (
      !recorder.current ||
      recorder.current.state === RECORDER_STATUS.inactive
    )
      return
    playSound('cancel-recording')
    recorder.current.stop()

    setStatus(RECORDER_STATUS.cancelled)
    logD.info('Cancelled recording')

    cleanup(options)
  }

  function handleChunk(e) {
    if (!e.data || !e.data.size) return

    chunkCount.current++

    const hitChunkLimit = chunkCount.current >= maxChunkCount

    hitChunkLimit ? stop() : sendChunk(e.data)
  }

  function cleanup(options = {}) {
    setError(null)

    if (options.status && RECORDER_STATUS[options.status]) {
      setStatus(RECORDER_STATUS[options.status])
    }

    removeRecorderListeners()

    chunkCount.current = 0

    if (stream.current) {
      stream.current.getTracks().forEach(track => track.stop())
      stream.current = null
    }

    uploaderCleanup(options)
  }

  function removeRecorderListeners() {
    if (recorder.current) {
      recorder.current.removeEventListener('dataavailable', handleChunk)
      recorder.current.removeEventListener('stop', () => stop())
      recorder.current = null
    }
  }

  function playSound(mp3FileName) {
    const audio = new Audio(getResourceFile(`/sounds/${mp3FileName}.mp3`))
    audio.play()
  }

  return {
    getStream,
    start,
    pause,
    stop,
    cancel,
    resume,
    status,
    error,
    uploaderError,
    cleanup,
    finalizeRecording: finalize,
    finalizeData,
    resetStatus,
    registeredMote,
    streamingTranscriptData
  }
}
